import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiCheck, HiX } from "react-icons/hi";
import { PageLayout, ActionButton, MultiLanguageTextInput } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import "./CreateMeasurementUnit.scss";

export const CreateMeasurementUnit: React.FC = () => {
  const { t, i18n } = useTranslation(['measurementUnits', 'common']);
  const navigate = useNavigate();
  const { token } = useAuth();
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState({
    name: "",
    symbol: "",
    type: 0,
    conversionFactorToBase: 1,
    isDefault: false,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    let newValue: any;
    if (type === 'checkbox') {
      newValue = checked;
    } else if (type === 'number') {
      newValue = parseFloat(value) || 0;
    } else if (name === 'type') {
      // Convert type field to integer
      newValue = parseInt(value, 10);
    } else {
      newValue = value;
    }

    let newFormData = {
      ...formData,
      [name]: newValue,
    };

    // If isDefault is being set to true, automatically set conversionFactorToBase to 1
    if (name === 'isDefault' && checked) {
      newFormData.conversionFactorToBase = 1;
    }

    setFormData(newFormData);

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      newErrors.name = t('measurementUnits:validation.nameRequired');
    }

    if (!formData.symbol.trim()) {
      newErrors.symbol = t('measurementUnits:validation.symbolRequired');
    }

    if (formData.conversionFactorToBase <= 0) {
      newErrors.conversionFactorToBase = t('measurementUnits:validation.conversionFactorRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitMeasurementUnit = async () => {
    if (!validateForm()) {
      showErrorToast(t('measurementUnits:validation.fillAllFields'));
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await adminApi.call('/measurementunit', 'POST', formData);

      if (!response.success) {
        showErrorToast(response.error || t('measurementUnits:messages.createError'));
        return;
      }

      showSuccessToast(t('measurementUnits:messages.createSuccess'));
      setFormData({ name: "", symbol: "", type: 0, conversionFactorToBase: 1, isDefault: false });
    } catch (error: any) {
      showErrorToast(t('measurementUnits:messages.createError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t('measurementUnits:breadcrumbs.measurementUnits'), href: '/measurement-units' },
    { label: t('measurementUnits:breadcrumbs.create') }
  ];

  return (
    <PageLayout
      title={t('measurementUnits:createTitle')}
      subtitle={t('measurementUnits:createSubtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="measurement-unit-form">
        <div className="row">
          <div className="col-md-6">
            <MultiLanguageTextInput
              label={t('measurementUnits:fields.name')}
              value={formData.name}
              onChange={(value) => {
                setFormData({ ...formData, name: value });
                if (errors.name) {
                  setErrors(prev => ({ ...prev, name: undefined }));
                }
              }}
              placeholder={t('measurementUnits:placeholders.name')}
              error={errors.name}
              disabled={loading}
              required
            />
          </div>

          <div className="col-md-6">
            <MultiLanguageTextInput
              label={t('measurementUnits:fields.symbol')}
              value={formData.symbol}
              onChange={(value) => {
                setFormData({ ...formData, symbol: value });
                if (errors.symbol) {
                  setErrors(prev => ({ ...prev, symbol: undefined }));
                }
              }}
              placeholder={t('measurementUnits:placeholders.symbol')}
              error={errors.symbol}
              disabled={loading}
              required
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label">{t('measurementUnits:fields.type')} <span className="required">*</span></label>
              <select
                className="form-control"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
              >
                <option value={0}>{t('measurementUnits:types.length')}</option>
                <option value={1}>{t('measurementUnits:types.weight')}</option>
              </select>
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label">{t('measurementUnits:fields.conversionFactor')} <span className="required">*</span></label>
              <input
                type="number"
                className={`form-control ${errors.conversionFactorToBase ? 'is-invalid' : ''}`}
                name="conversionFactorToBase"
                value={formData.conversionFactorToBase}
                onChange={handleInputChange}
                placeholder={t('measurementUnits:placeholders.conversionFactor')}
                min="0"
                step="0.000001"
                disabled={formData.isDefault}
                required
              />
              {errors.conversionFactorToBase && <div className="invalid-feedback">{errors.conversionFactorToBase}</div>}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  name="isDefault"
                  checked={formData.isDefault}
                  onChange={handleInputChange}
                  id="isDefault"
                />
                <label className="form-check-label" htmlFor="isDefault">
                  {t('measurementUnits:fields.isDefault')}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <ActionButton
            variant="primary"
            icon={HiCheck}
            onClick={submitMeasurementUnit}
            loading={loading}
            disabled={loading}
          >
            {loading ? t('measurementUnits:buttons.creating') : t('measurementUnits:buttons.create')}
          </ActionButton>
          
          <ActionButton
            variant="secondary"
            icon={HiX}
            onClick={() => navigate('/measurement-units')}
            disabled={loading}
          >
            {t('common:cancel')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
